import { PaymentControllerService } from '@/__generated__/CommonApi';
import btsImage from '@/assets/img/support/en_donate_bts@2x.png';
import imgSupportStep01 from '@/assets/img/support/support_step_01@2x.png';
import imgSupportStep02 from '@/assets/img/support/support_step_02@2x.png';
import imgSupportStep03 from '@/assets/img/support/support_step_03@2x.png';
import Agreement from '@/components/Agreement';
import Button from '@/components/Button';
import ErrorMessage from '@/components/Form/ErrorMessage';
import FormGroup from '@/components/Form/FormGroup';
import Label from '@/components/Form/Label';
import HR from '@/components/HR';
import InputNumber from '@/components/Input/InputNumber';
import InputRadio, { InputRadioGroup } from '@/components/Input/InputRadio';
import InputText from '@/components/Input/InputText';
import { NotiBox } from '@/components/Text/TextNoti';
import { H2, H4 } from '@/components/Titles';
import { EnglishPrivacyPolicyContent } from '@/data/agreements';
import { breakpoint } from '@/helpers/BreakpointHelper';
import { isMobileDevice } from '@/helpers/BrowserHelper';
import {
  INICIS_PAY_METHOD_MOBILE_MAP,
  INICIS_PAY_METHOD_PC_MAP,
  KCP_PAY_METHOD_MOBILE_MAP,
  KCP_PAY_METHOD_PC_MAP,
  PAYTYPE_ONCE_CARD,
} from '@/helpers/PaymentHelper';
import {
  ENUM_SUPPORTER_TYPE,
  PAGE_DONATE_ONMYMIND_EN,
  SOLICIT_CODE_ONMYMIND,
  SUPPORT_CATEGORY_ONCE_WORLD,
  SUPPORT_TERM_ONCE,
} from '@/helpers/SupportHelper';
import LayoutWithTitle from '@/layouts/LayoutWithTitle';
import { DonateFormItem } from '@/page-blocks/donate/DonateCommon';
import SessionStorage from '@/services/SessionStorage';
import { usePopupStore } from '@/stores/PopupStore';
import { PageProps } from 'gatsby';
import queryString from 'query-string';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';
import InicisMobileOnceForm, {
  InicisMobileOnceRef,
} from '@/components/ThirdParty/InicisMobileOnceForm';
import InicisPcOnceForm, {
  InicisPcOnceRef,
} from '@/components/ThirdParty/InicisPcOnceForm';
import KcpMobileForm, {
  KcpMobileRef,
} from '@/components/ThirdParty/KcpMobileForm';
import KcpPcForm, { KcpPcRef } from '@/components/ThirdParty/KcpPcForm';

const Container = styled.div`
  max-width: 544px;
  margin: 0 auto 24px;
  ${breakpoint(640)} {
    width: 100%;
    padding: 0 20px;
  }
`;
const StepHead = styled.div`
  line-height: 1.2;
  padding: 29px 0;
  color: #b7b8ba;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-end;
  position: relative;
`;
const StepBody = styled.div`
  // padding-bottom: 56px;
  display: none;
`;
const StepImage = styled.img<{ active?: boolean }>`
  width: 100%;
  height: 48px;
  box-sizing: content-box;
  object-fit: contain;
  padding: 24px 0;
  border-bottom: 1px solid #b7b8ba;
  display: none;
  ${({ active }) => {
    if (active) {
      return `
        display: block;
      `;
    }
    return ``;
  }}
`;
const ToggleBoxActive = css`
  ${H2} {
    color: #2d2926;
  }
  form {
    display: block;
  }
  ${StepBody} {
    display: block;
  }
`;
const ToggleBox = styled.div<{ active: boolean }>`
  ${H2} {
    color: #b7b8ba;
  }
  form {
    display: none;
  }
  border-bottom: 1px solid #b7b8ba;
  ${StepBody} {
    display: none;
  }
  ${NotiBox} {
    margin-bottom: 24px;
  }
  ${({ active }) => active && ToggleBoxActive};
`;
const FormResult = styled.div`
  color: #1cabe2;
  width: 100%;
  margin-top: 5px;
`;
const FormModify = styled.button`
  position: absolute;
  right: 0;
  top: 50%;
  line-height: 1;
  text-decoration: underline;
  color: #828385;
  font-size: 16px;
`;

const FormContainer = styled.div`
  ${InputRadioGroup} {
    ${breakpoint(`tablet`)} {
      ${InputText} {
        height: 44px;
      }
    }
  }
`;

const BtsBox = styled.div`
  background-color: #f8f9fa;
  padding: 20px;
  ${H4} {
    color: #1cabe2;
  }
  p {
    font-size: 16px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.75;
    letter-spacing: -0.8px;
  }
`;

const FormTotal = styled.div`
  position: relative;
  padding: 32px 0;
  margin: 56px 0 80px 0;
  border-top: 1px solid #b7b8ba;
  border-bottom: 1px solid #b7b8ba;
  ${H4} {
    position: absolute;
    left: 0;
    bottom: 16px;
    margin: 0;
  }
`;

const FormTotalPrice = styled.div`
  font-size: 32px;
  font-weight: bold;
  color: #1cabe2;
  text-align: right;
`;

interface EnSupportStep1Data {
  supportAmount: string;
  supportAmountInput: string;
}

interface EnDonateStep1Props {
  onSubmit: (data: EnSupportStep1Data) => void;
  queryParams: any;
}

const EnDonateStep1: FC<EnDonateStep1Props> = ({ onSubmit, queryParams }) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    setError,
    control,
  } = useForm<EnSupportStep1Data>({
    defaultValues: {
      supportAmount: queryParams.supportAmount || `30000`,
      supportAmountInput: queryParams.supportAmountInput || ``,
    },
  });

  const supportAmount = watch(`supportAmount`, `0`);
  const supportAmountInput = watch(`supportAmountInput`);
  const finalAmount = useMemo(
    () => Number(supportAmount || supportAmountInput),
    [supportAmount, supportAmountInput],
  );

  const onSubmitForm: SubmitHandler<EnSupportStep1Data> = useCallback(
    (formData) => {
      if (!finalAmount) {
        setError(`supportAmountInput`, {
          type: `required`,
          message: `Donate amount is required`,
          shouldFocus: true,
        });

        return;
      }
      onSubmit(formData);
    },
    [finalAmount, onSubmit, setError],
  );

  return (
    <FormContainer>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <HR color="#1cabe2" />
        <DonateFormItem
          css={`
            margin-bottom: 56px;
          `}
        >
          <H4>Donation Amount (won)</H4>
          <InputRadioGroup full>
            <InputRadio
              ref={register}
              name="supportAmount"
              tab
              value="30000"
              label="30,000"
              onFocus={() => setValue(`supportAmountInput`, ``)}
            />
            <InputRadio
              ref={register}
              name="supportAmount"
              tab
              value="50000"
              label="50,000"
              onFocus={() => setValue(`supportAmountInput`, ``)}
            />
            <InputRadio
              ref={register}
              name="supportAmount"
              tab
              value="100000"
              label="100,000"
              onFocus={() => setValue(`supportAmountInput`, ``)}
            />
            <InputRadio
              ref={register}
              name="supportAmount"
              tab
              value="200000"
              label="200,000"
              onFocus={() => setValue(`supportAmountInput`, ``)}
            />
          </InputRadioGroup>
          <Controller
            control={control}
            name="supportAmountInput"
            rules={{
              validate: (value) => {
                if (Number(finalAmount) < 1000) {
                  return `Amounts under 1,000 KRW cannot be accepted.`;
                }
              },
            }}
            as={
              <InputNumber
                allowNegative={false}
                onFocus={() => setValue(`supportAmount`, ``)}
                placeholder="Please select or enter your Donation amount."
                css={`
                  margin-top: 8px;
                `}
              />
            }
          />
          {errors.supportAmountInput && (
            <ErrorMessage>{errors.supportAmountInput.message}</ErrorMessage>
          )}
          <img
            src={btsImage}
            alt="BTS OnMyMind"
            width="100%"
            css={`
              margin-top: 24px;
            `}
          />
          <BtsBox>
            <H4>#OnMyMind</H4>
            <p>
              UNICEF works in 190 countries and territories to save
              children&apos;s lives. To defend their rights. To help them fulfil
              their potential. <br />
              And we never give up. UNICEF, for every child.
            </p>
          </BtsBox>
          <FormTotal>
            <H4>Donation Amount</H4>
            <FormTotalPrice>
              {Number(finalAmount).toLocaleString()}won
            </FormTotalPrice>
          </FormTotal>
          <Button type="submit" full>
            next
          </Button>
        </DonateFormItem>
      </form>
    </FormContainer>
  );
};

interface EnSupportStep2Data {
  memberName: string;
  email: string;
  mobilePhoneNumber: string;
  birthday: string;
  policyAgree: boolean;
}

interface EnDonateStep2Props {
  onSubmit: (data: EnSupportStep2Data) => void;
}

const EnDonateStep2: FC<EnDonateStep2Props> = ({ onSubmit }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EnSupportStep2Data>({
    defaultValues: {
      memberName: ``,
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <HR color="#1cabe2" />
      <DonateFormItem>
        <H4>Your Information</H4>
        <FormGroup>
          <Label required>Full Name</Label>
          <InputText
            ref={register({
              required: {
                value: true,
                message: `Required input`,
              },
            })}
            name="memberName"
            placeholder="Enter your full name"
          />
          {errors.memberName && (
            <ErrorMessage>{errors.memberName.message}</ErrorMessage>
          )}
        </FormGroup>
        <FormGroup>
          <Label required>Email Address</Label>
          <InputText
            ref={register({
              required: {
                value: true,
                message: `Required input`,
              },
            })}
            name="email"
            placeholder="Enter your email address"
          />
          {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
        </FormGroup>
        <FormGroup>
          <Label required>Mobile/Cell number</Label>
          <InputText
            ref={register({
              required: {
                value: true,
                message: `Required input`,
              },
            })}
            name="mobilePhoneNumber"
            placeholder="Enter your number without hyphens (-)"
          />
          {errors.mobilePhoneNumber && (
            <ErrorMessage>{errors.mobilePhoneNumber.message}</ErrorMessage>
          )}
        </FormGroup>
        <FormGroup
          css={`
            margin-bottom: 40px;
          `}
        >
          <Label required>Date of birth</Label>
          <InputText
            ref={register({
              required: {
                value: true,
                message: `Required input`,
              },
            })}
            name="birthday"
            placeholder="Enter 8 digits in the format of YYYYMMDD"
          />
          {errors.birthday && (
            <ErrorMessage>{errors.birthday.message}</ErrorMessage>
          )}
        </FormGroup>
        <StepHead>
          <H2>The collection and use of personal information</H2>
        </StepHead>
        <HR
          color="#b7b8ba"
          css={`
            margin-bottom: 20px;
          `}
        />
        <Agreement
          name="policyAgree"
          ref={register({
            required: {
              value: true,
              message: `Please agree to the terms and conditions`,
            },
          })}
          policies={[
            {
              required: true,
              label: `Privacy Policy`,
              content: EnglishPrivacyPolicyContent,
            },
          ]}
          labels={{
            agreeAll: ``,
            required: ``,
            optional: ``,
            more: `more`,
          }}
        />
        {errors.policyAgree && (
          <ErrorMessage>{errors.policyAgree.message}</ErrorMessage>
        )}
        <Button
          type="submit"
          color="yellow"
          full
          css={`
            margin: 92px 0;
          `}
          disabled={isSubmitting}
        >
          Donate Now
        </Button>
      </DonateFormItem>
    </form>
  );
};

interface EnSupportStep3Data {
  // 신용카드 정보
  cardNumber1: string;
  cardNumber2: string;
  cardNumber3: string;
  cardNumber4: string;
  cardExpireM: string;
  cardExpireY: string;
  // 이용약관
  policyAgree: boolean;
  // 개발환경용 생년월일 필드
  depositorBirthday: string;
}
interface EnDonateStep3Props {
  onSubmit: (data: EnSupportStep3Data) => void;
}

type SubmitParams = Parameters<
  typeof PaymentControllerService.paymentInfoSetUsingPost
>[0];
// 고정데이터 (영문 onmymind 전용)
const FixedData = {
  productName: '후원하기',
  supportTerm: SUPPORT_TERM_ONCE,
  supportCategory: SUPPORT_CATEGORY_ONCE_WORLD,
  supporterType: ENUM_SUPPORTER_TYPE.person,
  memberTypeCode: `IN`,
  solicitCode: SOLICIT_CODE_ONMYMIND,
  paymentType: PAYTYPE_ONCE_CARD,
  deviceType: isMobileDevice() ? `MOBILE` : `PC`,
  donationCode: SUPPORT_CATEGORY_ONCE_WORLD,
  supportTypeCode: SUPPORT_TERM_ONCE,
  paymentTypeCode: PAYTYPE_ONCE_CARD,
  pathType: 'SUPPORT',
  moduleCode: `EMPTY`,
  mobileAgreeYn: 'N', // SF 도입전에 기본 N으로 셋팅
  emailAgreeYn: 'N', // SF 도입전에 기본 N으로 셋팅
  postAgreeYn: 'N', // SF 도입전에 기본 N으로 셋팅
  nationCode: `EN`,
  pgType: process.env.PG_TYPE_DONATE_EN || 'KCP',
  lang: `EN`,
  useBillPaymentYn: 'N',
  successUrl: `${process.env.SELF_URL}${PAGE_DONATE_ONMYMIND_EN}/complete/`,
};

const EnDonatePage: FC<PageProps> = ({ location }) => {
  const popupStore = usePopupStore();
  const [currentStep, setCurrentStep] = useState(1);
  const [step1Data, setStep1Data] = useState<EnSupportStep1Data>();
  const [step2Data, setStep2Data] = useState<EnSupportStep2Data>();
  const totalData = useMemo(() => ({ ...step1Data, ...step2Data }), [
    step1Data,
    step2Data,
  ]);
  const queryParams = queryString.parse(location.search);
  const confPgType = process.env.PG_TYPE_DONATE_EN || 'KCP'; // 후원하기 페이지용 PG사
  const inicisPcRef = useRef<InicisPcOnceRef>(null);
  const inicisMobileRef = useRef<InicisMobileOnceRef>(null);
  const kcpPcRef = useRef<KcpPcRef>(null);
  const kcpMobileRef = useRef<KcpMobileRef>(null);

  // 총 후원금액
  const finalAmount = useMemo(() => {
    const { supportAmount, supportAmountInput } = totalData;
    return Number(supportAmount || supportAmountInput || 0);
  }, [totalData]);

  const onStep1Submit: SubmitHandler<EnSupportStep1Data> = useCallback(
    (data) => {
      setStep1Data(data);
      setCurrentStep(2);
    },
    [],
  );

  const onStep2Submit: SubmitHandler<EnSupportStep2Data> = useCallback(
    async (data) => {
      // GtmHelper.checkoutStep2({
      //   supporterType: FixedData.supporterType,
      //   memberTypeCode: FixedData.memberTypeCode,
      // });
      setStep2Data(data);

      // 고정데이터와 폼데이터 병합
      const finalData: SubmitParams = {
        ...data,
        ...totalData,
        ...FixedData,
        trackcode: SessionStorage.getItem(`trackcode`) || undefined,
        trkId: SessionStorage.getItem(`trk_id`) || undefined,
        totalAmount: finalAmount,
      };

      // 전송 먼저
      const {
        resultCode,
        resultMessage,
        orderToken,
        orderNumber,
        mkey,
        signature,
        timestamp,
        kcpInfo,
      } = await PaymentControllerService.paymentInfoSetUsingPost(finalData);

      // 주문번호 생성에 성공하면
      if (resultCode === `success`) {
        // 주문토큰 저장
        SessionStorage.setItem(`orderToken`, orderToken || '');
        // trackcode, trk_id, solicitcode 비우기
        SessionStorage.removeItem(`trackcode`);
        SessionStorage.removeItem(`trk_id`);
        SessionStorage.removeItem(`solicitcode`);
        // PG결제
        // 모바일이면
        if (isMobileDevice()) {
          if (confPgType === 'KCP') {
            if (
              !finalData ||
              !orderNumber ||
              !kcpInfo?.siteId ||
              !kcpInfo?.siteName ||
              !kcpInfo?.approvalKey ||
              !kcpInfo?.traceNo ||
              !kcpInfo?.payUrl ||
              !kcpInfo?.retUrl
            ) {
              popupStore.show(`주요정보가 넘어오지 않았습니다`);
              return;
            }
            kcpMobileRef.current?.init({
              pgProc: 'pay',
              siteCd: kcpInfo?.siteId,
              siteName: kcpInfo?.siteName,
              siteLogo: kcpInfo?.siteLogo || '',
              approvalKey: kcpInfo?.approvalKey,
              traceNo: kcpInfo?.traceNo,
              payUrl: kcpInfo?.payUrl,
              retUrl: kcpInfo?.retUrl,
              payMethod: KCP_PAY_METHOD_MOBILE_MAP[finalData.paymentTypeCode],
              goodname: finalData?.productName || '',
              buyername: finalData?.memberName || '',
              buyeremail: finalData?.email,
              buyertel: finalData.mobilePhoneNumber,
              price: finalData?.totalAmount || 0,
              oid: orderNumber,
              completeUrl: finalData?.successUrl,
              failUrl: window.location.href,
              gender: '',
              missModuleCode: '',
              missUserId: '',
              solicitCode: '',
              currency: '',
            });
          } else {
            if (!finalData || !orderNumber) {
              console.error(`주요정보가 넘어오지 않았습니다`);
              return;
            }
            inicisMobileRef.current?.init({
              payMethod:
                INICIS_PAY_METHOD_MOBILE_MAP[finalData.paymentTypeCode],
              goods: finalData?.productName,
              uname: finalData?.memberName,
              amt: finalData?.totalAmount || 0,
              oid: orderNumber,
              lang: finalData.lang || 'EN',
            });
          }
        } else {
          if (confPgType === 'KCP') {
            if (
              !finalData ||
              !orderNumber ||
              !kcpInfo?.siteId ||
              !kcpInfo?.siteName ||
              !kcpInfo?.jsPlugin
            ) {
              popupStore.show(`주요정보가 넘어오지 않았습니다`);
              return;
            }
            kcpPcRef.current?.init({
              pgProc: 'pay',
              siteCd: kcpInfo?.siteId,
              siteName: kcpInfo?.siteName,
              siteLogo: kcpInfo?.siteLogo || '',
              jsPluginUrl: kcpInfo?.jsPlugin,
              payMethod: KCP_PAY_METHOD_PC_MAP[finalData.paymentTypeCode],
              goodname: finalData?.productName || '',
              buyername: finalData?.memberName || '',
              buyeremail: finalData?.email,
              buyertel: finalData.mobilePhoneNumber,
              price: finalData?.totalAmount || 0,
              oid: orderNumber,
              completeUrl: finalData?.successUrl,
              failUrl: window.location.origin,
              gender: '',
              missModuleCode: '',
              missUserId: '',
              solicitCode: '',
              currency: '',
            });
          } else {
            if (
              !finalData ||
              !orderNumber ||
              !mkey ||
              !signature ||
              !timestamp
            ) {
              console.error(`주요정보가 넘어오지 않았습니다`);
              return;
            }
            inicisPcRef.current?.init({
              payMethod: INICIS_PAY_METHOD_PC_MAP[finalData.paymentTypeCode],
              goodname: finalData?.productName,
              buyername: finalData?.memberName,
              buyeremail: finalData?.email,
              buyertel: finalData?.mobilePhoneNumber,
              price: finalData?.totalAmount,
              mKey: mkey,
              oid: orderNumber,
              signature,
              timestamp,
              gender: '',
              missModuleCode: '',
              missUserId: '',
              solicitCode: '',
              lang: finalData.lang || 'EN',
            });
          }
        }
      }
      // 주문번호 생성에 실패하면
      else if (resultMessage) {
        popupStore.show(resultMessage);
      }
    },
    [finalAmount, popupStore, totalData],
  );

  return (
    <LayoutWithTitle location={location} title="Donate" hideGnb>
      <Container>
        <StepImage
          src={imgSupportStep01}
          active={currentStep === 1}
          alt="Donate Step1"
        />
        <StepImage
          src={imgSupportStep02}
          active={currentStep === 2}
          alt="Donate Step2"
        />
        <StepImage
          src={imgSupportStep03}
          active={currentStep === 3}
          alt="Donate Step3"
        />
        <ToggleBox active={currentStep === 1}>
          <StepHead>
            <H2>1. Donation Amount</H2>
            {currentStep !== 1 && (
              <FormModify type="button" onClick={() => setCurrentStep(1)}>
                edit{` `}
              </FormModify>
            )}
            <FormResult>
              {finalAmount ? `${finalAmount.toLocaleString()} won` : null}
            </FormResult>
          </StepHead>
          <StepBody>
            <EnDonateStep1 queryParams={queryParams} onSubmit={onStep1Submit} />
          </StepBody>
        </ToggleBox>
        <ToggleBox active={currentStep === 2}>
          <StepHead>
            <H2>2. Your Information</H2>
            <FormModify type="button" onClick={() => setCurrentStep(2)}>
              edit{` `}
            </FormModify>
            <FormResult>{step2Data?.memberName}</FormResult>
          </StepHead>
          <StepBody>
            <EnDonateStep2 onSubmit={onStep2Submit} />
          </StepBody>
        </ToggleBox>
        {confPgType === 'KCP' ? (
          <>
            <KcpPcForm ref={kcpPcRef} />
            <KcpMobileForm ref={kcpMobileRef} />
          </>
        ) : (
          <>
            <InicisPcOnceForm ref={inicisPcRef} />
            <InicisMobileOnceForm ref={inicisMobileRef} />
          </>
        )}
      </Container>
    </LayoutWithTitle>
  );
};

export default EnDonatePage;
